import React from "react";
import {Steps, Tooltip} from "antd";
import {UtilsService} from "../../services/Utils.service";
import {
    ATTESA_PAGAMENTO_REASON_LABELS,
    FileTsAuditDto,
    getArchiveReasonLabel,
    getSuspensionReasonLabel,
    PAID_REASON_LABELS,
    stepIndexMap
} from "../../model/models";

interface PracticeDetailPanelStepsProps {
    status: string;
    communicationDate?: string;
    auditTrail?: FileTsAuditDto[];
}

export default function PracticeDetailPanelSteps({
                                                     status,
                                                     communicationDate,
                                                     auditTrail = [],
                                                 }: PracticeDetailPanelStepsProps) {
    const currentStepIndex = stepIndexMap[status] ?? 0;

    const sortedAuditTrail = [...auditTrail].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );

    const stepsItem = [
        { title: "Notifica" },
        { title: "Attesa Pagamento" },
        { title: "Pagate" },
        { title: "Sospese" },
        { title: "Scadute" },
        { title: "Trasferite" },
        { title: "Archiviate" },
        { title: "Da Rendicontare" },
        { title: "Rendicontate" },
    ];

    const stepDescriptions = stepsItem.map((step) => {
        const audit = sortedAuditTrail.find((a) =>
            step.title.toUpperCase().includes(a.status.description.toUpperCase())
        );
        if (!audit) return undefined;

        const updatedBy = audit.updatedBy ? `${audit.updatedBy.nome} ${audit.updatedBy.cognome}` : "";
        const updatedAt = UtilsService.formatDateString(audit.updatedAt);

        let lines: React.ReactNode[] = [];

        if (step.title === "Notifica") {
            if (updatedBy) {
                lines.push(
                    <div key="by" style={{ fontSize: "10px", color: "#888" }}>
                        {updatedBy}
                    </div>
                );
            }
            if (communicationDate) {
                lines.push(
                    <div key="communication" style={{ fontSize: "10px", color: "#888" }}>
                        {UtilsService.formatDateString(communicationDate)}
                    </div>
                );
            }
        } else {
            if (updatedBy) {
                lines.push(
                    <div key="by" style={{ fontSize: "10px", color: "#888" }}>
                        {updatedBy}
                    </div>
                );
            }
            if (updatedAt) {
                lines.push(
                    <div key="at" style={{ fontSize: "10px", color: "#888" }}>
                        {updatedAt}
                    </div>
                );
            }

            if (step.title === "Sospese" && audit.metadata?.causaSospensione) {
                const causa = audit.metadata.causaSospensione;
                const label = getSuspensionReasonLabel(causa);
                const shortText = causa.length > 20 ? `${label.substring(0, 20)}...` : label;
                lines.push(
                    <Tooltip key="causa" title={label}>
                        <div style={{ fontSize: "10px", color: "#888" }}>{shortText}</div>
                    </Tooltip>
                );
            }

            if (step.title === "Archiviate" && audit.metadata?.causaArchiviazione) {
                const causa = audit.metadata.causaArchiviazione;
                const label = getArchiveReasonLabel(causa);
                const shortText = causa.length > 20 ? `${label.substring(0, 20)}...` : label;
                lines.push(
                    <Tooltip key="causa" title={label}>
                        <div style={{ fontSize: "10px", color: "#888" }}>{shortText}</div>
                    </Tooltip>
                );
            }

            if (step.title === "Attesa Pagamento" && audit.metadata?.causaAttesaPagamento) {
                const causa = ATTESA_PAGAMENTO_REASON_LABELS[audit.metadata.causaAttesaPagamento] || audit.metadata.causaAttesaPagamento;
                const shortText = causa.length > 20 ? `${causa.substring(0, 20)}...` : causa;
                lines.push(
                    <Tooltip key="causa" title={causa}>
                        <div style={{ fontSize: "10px", color: "#888" }}>{shortText}</div>
                    </Tooltip>
                );
            }

            if (step.title === "Pagate" && audit.metadata?.causaPagate) {
                const causa = PAID_REASON_LABELS[audit.metadata.causaPagate];
                const shortText = causa.length > 30 ? `${causa.substring(0, 30)}...` : causa;
                lines.push(
                    <Tooltip key="causa" title={causa}>
                        <div style={{ fontSize: "10px", color: "#888" }}>{shortText}</div>
                    </Tooltip>
                );
            }
        }

        return lines.length > 0 ? <>{lines}</> : undefined;
    });

    const stepStatuses = stepsItem.map((step, index) => {
        if (index < currentStepIndex) {
            const found = sortedAuditTrail.some((a) =>
                step.title.toUpperCase().includes(a.status.description.toUpperCase())
            );
            return found ? "finish" : "error";
        }
        if (index === currentStepIndex) return "process";
        return "wait";
    });

    return (
        <Steps
            labelPlacement="vertical"
            current={currentStepIndex}
            items={stepsItem.map((step, index) => ({
                title: step.title,
                description: stepDescriptions[index],
                status: stepStatuses[index],
            }))}
        />
    );
}
